import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../firebaseConfig';
import { Gift, Printer, Mail, MapPin, Clock } from 'lucide-react';

export default function GiftCertificate() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [giftData, setGiftData] = useState(null);

  useEffect(() => {
    async function fetchGiftCode() {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const sessionRef = ref(db, `giftSessions/${sessionId}`);
        const sessionSnap = await get(sessionRef);

        if (!sessionSnap.exists()) {
          setError('Gift code not found. Please contact support.');
          setLoading(false);
          return;
        }

        const { giftCode } = sessionSnap.val();
        const giftRef = ref(db, `giftCodes/${giftCode}`);
        const giftSnap = await get(giftRef);

        if (!giftSnap.exists()) {
          setError('Gift code not found. Please contact support.');
          setLoading(false);
          return;
        }

        const data = giftSnap.val();
        setGiftData({
          code: giftCode,
          ...data
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching gift code:', err);
        setError('Failed to load gift certificate. Please try again.');
        setLoading(false);
      }
    }

    fetchGiftCode();
  }, [sessionId]);

  const getTourDisplayName = (tourId) => {
    const names = {
      'san-juan': 'San Juan, Puerto Rico',
      'mexico-city-scavenger-hunt-tour': 'Mexico City, Mexico',
      'chicago-scavenger-hunt-tour': 'Chicago, Illinois'
    };
    return names[tourId] || 'Tour in a Box';
  };

  const getTourDetails = (tourId) => {
    const details = {
      'mexico-city-scavenger-hunt-tour': {
        stops: 10,
        duration: '2 hours',
        difficulty: 'Easy to moderate',
        startPoint: 'JW Marriott, Polanco',
        highlights: [
          '10 interactive riddles at historic locations',
          '2-hour self-paced adventure through Polanco',
          'Historical facts and hidden stories at each stop',
          'Works on any smartphone - no app required',
          'Play anytime within 1 year',
          'Offline mode available after download'
        ]
      },
      'san-juan': {
        stops: 12,
        duration: '2.5 hours',
        difficulty: 'Easy to moderate',
        startPoint: 'Old San Juan',
        highlights: [
          '12 interactive riddles through Old San Juan',
          '2.5-hour self-paced colorful adventure',
          'Discover hidden gems and local history',
          'Works on any smartphone - no app required',
          'Play anytime within 1 year',
          'Offline mode available after download'
        ]
      },
      'chicago-scavenger-hunt-tour': {
        stops: 11,
        duration: '1 hour',
        difficulty: 'Easy to moderate',
        startPoint: 'Art Institute of Chicago',
        highlights: [
          '11 interactive spy mission puzzles',
          '1-hour tactical mission through downtown Chicago',
          'Iconic landmarks and architectural wonders',
          'Works on any smartphone - no app required',
          'Play anytime within 1 year'
        ]
      }
    };
    return details[tourId] || details['mexico-city-scavenger-hunt-tour'];
  };

  const formatExpirationDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmailMe = () => {
    const subject = encodeURIComponent('Your Tour in a Box Gift Certificate');
    const body = encodeURIComponent(
      `Your gift certificate for ${getTourDisplayName(giftData.tourId)} is ready!\n\n` +
      `Gift Code: ${giftData.code}\n\n` +
      `Access your certificate anytime at:\n${window.location.href}\n\n` +
      `Redeem at: https://play.tourinabox.com\n\n` +
      `Questions? Email hello@tourinabox.com`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFADF' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#F8662B' }}></div>
          <p style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>Loading your gift certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFADF' }}>
        <div className="max-w-md text-center p-8">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}>
            Oops!
          </h1>
          <p className="mb-6" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
            {error}
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-xl text-white font-semibold"
            style={{ backgroundColor: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const tourDetails = getTourDetails(giftData.tourId);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #FFFADF 0%, #FFE36E 100%)' }}>
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.25in;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          html, body {
            height: 100%;
            margin: 0;
            padding: 0;
            background: white !important;
          }

          .min-h-screen {
            background: white !important;
            min-height: auto !important;
          }

          .no-print {
            display: none !important;
          }

          .print-only {
            display: block !important;
          }

          .certificate-outer {
            padding: 0 !important;
          }

          .certificate-container {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            box-shadow: none !important;
            margin: 0 !important;
            max-width: 100% !important;
            border-radius: 0 !important;
            border-width: 6px !important;
          }

          .certificate-body {
            padding: 1.25rem 1.5rem !important;
          }

          .tour-title {
            font-size: 2rem !important;
          }

          .tour-subtitle {
            font-size: 1rem !important;
          }

          .gift-code-display {
            font-size: 2rem !important;
          }

          .two-col-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 1rem !important;
          }

          .redemption-steps {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 0.5rem !important;
          }

          .redemption-steps li {
            flex: 1 1 45% !important;
            margin-bottom: 0 !important;
          }

          .step-number {
            width: 1.25rem !important;
            height: 1.25rem !important;
            font-size: 0.7rem !important;
          }

          .step-text {
            font-size: 0.75rem !important;
          }

          .section-compact {
            padding: 0.75rem !important;
            margin-bottom: 0.75rem !important;
          }

          .footer-section {
            padding-top: 0.5rem !important;
            margin-top: 0.5rem !important;
          }

          .footer-section p {
            font-size: 0.7rem !important;
            margin-bottom: 0.125rem !important;
          }
        }

        .print-only {
          display: none;
        }

        .decorative-border {
          background:
            repeating-linear-gradient(45deg, #F8662B 0, #F8662B 10px, #FFFADF 10px, #FFFADF 20px, #385B37 20px, #385B37 30px, #FFFADF 30px, #FFFADF 40px);
          background-size: 56.57px 56.57px;
        }
      `}</style>

      {/* Header - Hidden on print */}
      <header className="no-print py-6 px-4 border-b" style={{ background: '#FFFADF', borderColor: '#E4B4A6' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img
            src="/PrimaryLogo.png"
            alt="Tour in a Box"
            className="h-8"
            style={{ objectFit: 'contain' }}
          />
          <Link to="/" className="text-sm font-semibold" style={{ color: '#F8662B', fontFamily: 'Urbanist, sans-serif' }}>
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Success Message - Hidden on print */}
      <div className="no-print max-w-4xl mx-auto px-4 pt-8 pb-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: '#385B37' }}>
            <Gift className="text-white" size={32} />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}>
            Gift Purchase Complete!
          </h1>
          <p className="text-lg" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
            Your gift certificate is ready to print or share
          </p>
        </div>
      </div>

      {/* Action Buttons - Hidden on print */}
      <div className="no-print max-w-4xl mx-auto px-4 pb-8">
        <div className="flex flex-col items-center gap-4">
          {/* Primary Action */}
          <button
            onClick={handlePrint}
            className="flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-white transition-all hover:opacity-90 shadow-lg text-lg"
            style={{ backgroundColor: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
          >
            <Printer size={24} />
            Print Gift Certificate
          </button>

          {/* Secondary Actions */}
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={handleEmailMe}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all text-sm"
              style={{
                backgroundColor: '#FFFADF',
                color: '#385B37',
                border: '2px solid #E4B4A6',
                fontFamily: 'Urbanist, sans-serif'
              }}
            >
              <Mail size={18} />
              Email to myself so I can print later
            </button>
          </div>

          <p className="text-xs text-center mt-2" style={{ color: '#385B37', opacity: 0.7, fontFamily: 'Urbanist, sans-serif' }}>
            💡 Tip: Use your browser's "Print to PDF" to save as a file
          </p>
        </div>
      </div>

      {/* Gift Certificate */}
      <div className="certificate-outer max-w-4xl mx-auto px-4 pb-12">
        {/* Decorative border frame */}
        <div className="decorative-border p-2 rounded-2xl shadow-2xl">
          <div
            className="certificate-container bg-white rounded-xl overflow-hidden"
            style={{ border: '3px solid #385B37' }}
          >
            {/* Header with festive banner */}
            <div className="py-4 px-6 text-center" style={{ background: 'linear-gradient(135deg, #385B37 0%, #4a7249 100%)' }}>
              <h2
                className="text-2xl font-bold text-white mb-2"
                style={{ fontFamily: 'Rubik, sans-serif', letterSpacing: '0.1em' }}
              >
                GIFT CERTIFICATE
              </h2>
              <img
                src="/PrimaryLogo.png"
                alt="Tour in a Box"
                className="h-8 mx-auto"
                style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
              />
            </div>

            {/* Certificate Body */}
            <div className="certificate-body p-6 sm:p-8">
              {/* Personalization - Compact */}
              {(giftData.personalization?.to || giftData.personalization?.from || giftData.personalization?.message) && (
                <div className="section-compact mb-4 p-4 rounded-xl text-center" style={{ backgroundColor: '#FFFADF', border: '2px dashed #E4B4A6' }}>
                  {giftData.personalization.to && (
                    <p className="text-base mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                      To: <span className="font-bold">{giftData.personalization.to}</span>
                    </p>
                  )}
                  {giftData.personalization.message && (
                    <p className="text-sm italic mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                      "{giftData.personalization.message}"
                    </p>
                  )}
                  {giftData.personalization.from && (
                    <p className="text-base" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                      From: <span className="font-bold">{giftData.personalization.from}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Tour Name - Prominent */}
              <div className="text-center mb-4">
                <p className="text-sm uppercase tracking-widest mb-1" style={{ color: '#8FB3D3', fontFamily: 'Urbanist, sans-serif' }}>
                  Your Adventure Awaits In
                </p>
                <h3
                  className="tour-title text-3xl sm:text-4xl font-bold mb-1"
                  style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                >
                  {getTourDisplayName(giftData.tourId)}
                </h3>
                <p className="tour-subtitle text-lg" style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}>
                  Interactive Scavenger Hunt Adventure
                </p>
              </div>

              {/* Two Column Layout: Stats + Gift Code */}
              <div className="two-col-grid grid sm:grid-cols-2 gap-4 mb-4">
                {/* Quick Stats */}
                <div className="section-compact p-4 rounded-xl" style={{ backgroundColor: '#FFFADF' }}>
                  <div className="flex justify-around text-center">
                    <div>
                      <MapPin size={20} className="mx-auto mb-1" style={{ color: '#F8662B' }} />
                      <div className="font-bold text-lg" style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}>
                        {tourDetails.stops}
                      </div>
                      <div className="text-xs" style={{ color: '#385B37', opacity: 0.7 }}>Stops</div>
                    </div>
                    <div>
                      <Clock size={20} className="mx-auto mb-1" style={{ color: '#F8662B' }} />
                      <div className="font-bold text-lg" style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}>
                        {tourDetails.duration}
                      </div>
                      <div className="text-xs" style={{ color: '#385B37', opacity: 0.7 }}>Duration</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t text-center" style={{ borderColor: '#E4B4A6' }}>
                    <p className="text-xs" style={{ color: '#385B37' }}>
                      <span className="font-semibold">Start:</span> {tourDetails.startPoint}
                    </p>
                  </div>
                </div>

                {/* Gift Code - Prominent */}
                <div
                  className="section-compact p-4 rounded-xl text-center flex flex-col justify-center"
                  style={{ backgroundColor: '#385B37' }}
                >
                  <p className="text-xs uppercase tracking-wide mb-1 text-white opacity-80">
                    Gift Code
                  </p>
                  <p
                    className="gift-code-display text-2xl sm:text-3xl font-bold text-white tracking-wider mb-1"
                    style={{ fontFamily: 'monospace' }}
                  >
                    {giftData.code}
                  </p>
                  <p className="text-xs text-white opacity-70">
                    Valid until {formatExpirationDate(giftData.expiresAt)}
                  </p>
                </div>
              </div>

              {/* Redemption Instructions - Compact horizontal */}
              <div className="section-compact p-4 rounded-xl mb-4" style={{ backgroundColor: '#FFFADF' }}>
                <h4
                  className="text-sm font-bold mb-3 text-center"
                  style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}
                >
                  How to Redeem
                </h4>
                <ol className="redemption-steps grid grid-cols-2 gap-2" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                  <li className="flex items-center gap-2">
                    <span
                      className="step-number flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: '#F8662B' }}
                    >
                      1
                    </span>
                    <span className="step-text text-xs">Visit <strong>play.tourinabox.com</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span
                      className="step-number flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: '#F8662B' }}
                    >
                      2
                    </span>
                    <span className="step-text text-xs">Create account or log in</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span
                      className="step-number flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: '#F8662B' }}
                    >
                      3
                    </span>
                    <span className="step-text text-xs">Profile → "Redeem Code"</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span
                      className="step-number flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: '#F8662B' }}
                    >
                      4
                    </span>
                    <span className="step-text text-xs">Enter code & explore! 🎉</span>
                  </li>
                </ol>
              </div>

              {/* Footer - Compact */}
              <div className="footer-section text-center text-xs pt-3 border-t" style={{ borderColor: '#E4B4A6', color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                <p className="font-semibold">Tour in a Box</p>
                <p className="opacity-70">hello@tourinabox.com • play.tourinabox.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Footer */}
      <div className="print-only text-center text-sm py-4" style={{ color: '#385B37' }}>
        <p>© {new Date().getFullYear()} Tour in a Box LLC | play.tourinabox.com</p>
      </div>
    </div>
  );
}
